import {
  API_KEY_API3,
  getStandardErrorObject,
  getTransactionId,
  logError,
  postRequestForm,
  getLoggingHeaders,
  ENV,
  FORM_INPUT_ID_MAPPING,
  flattenObject,
  getURLConfigs,
  fetchWithTimeout,
} from './Utils.js';
import { ClientErrors } from './ClientErrors.js';

export function getCookiePreferencesApiCall(accountId, token) {
  return new Promise((resolve, reject) => {
    fetchWithTimeout(
      `${getURLConfigs().BASE_URL_SFLY_ACCOUNT_API}/v3/accounts/preferences?accountId=${accountId}`,
      {
        // credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-api-key': API_KEY_API3[ENV],
          ...getLoggingHeaders(accountId),
        },
      },
    )
      .then((result) => {
        if (result.status === 200) {
          result.json().then((data) => {
            const postUrl = `${getURLConfigs().BASE_URL_SFLY_ACCOUNT_API}${data.href}/cookieConsent?action=multi`;
            resolve(postUrl);
          });
        } else {
          const transId = getTransactionId();
          // Response's status/statusText/url are prototype getters, not own
          // properties, so passing `result` itself loses them - pull them out
          // explicitly.
          logError(
            ClientErrors.GET_PREFERENCES_FAILED,
            {
              status: result.status,
              statusText: result.statusText,
              url: result.url,
            },
            transId,
          );
          reject(
            getStandardErrorObject(
              ClientErrors.GET_PREFERENCES_FAILED,
              transId,
            ),
          );
        }
      })
      .catch((e) => {
        console.log(e);
        // Get a transId to link errors from the client to splunk
        const transId = getTransactionId();
        logError(
          ClientErrors.GET_PREFERENCES_FAILED,
          {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
            ...(e.response ?? {}),
          },
          transId,
        );
        reject(
          getStandardErrorObject(ClientErrors.GET_PREFERENCES_FAILED, transId),
        );
      });
  });
}

export async function postPreferencesCookiesApiCall(
  currentUser,
  postUrl,
  token,
) {
  return new Promise(async (resolve, reject) => {
    let body;
    let purposes;
    try {
      purposes = window.airgap.export().consent.purposes;

      body = [
        {
          channelType: 'ANALYTICS',
          lastModifiedBy: 'accountSettings',
          optIn: !!purposes.Analytics,
        },
        {
          channelType: 'SALEOFINFO',
          lastModifiedBy: 'accountSettings',
          optIn: !!purposes.SaleOfInfo,
        },
        {
          channelType: 'ADVERTISING',
          lastModifiedBy: 'accountSettings',
          optIn: !!purposes.Advertising,
        },
        {
          channelType: 'FUNCTIONAL',
          lastModifiedBy: 'accountSettings',
          optIn: !!purposes.Functional,
        },
      ];
    } catch (e) {
      const transId = getTransactionId();
      logError(
        ClientErrors.AIRGAP_ERROR,
        //Caught airgap error
        e,
        transId,
      );
      reject(getStandardErrorObject(ClientErrors.AIRGAP_ERROR, transId));
      return;
    }

    await fetchWithTimeout(postUrl, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-api-key': API_KEY_API3[ENV],
        ...getLoggingHeaders(currentUser?.sfly_uid),
      },
      body: JSON.stringify(body),
    })
      .then(async (result) => {
        if (result.status === 201) {
          resolve();
        } else {
          const transId = getTransactionId();
          // Response's status/statusText/url are prototype getters, not own
          // properties, so passing `result` itself loses them - pull them out
          // explicitly.
          logError(
            ClientErrors.POST_PREFERENCES_API_FAILED,
            {
              status: result.status,
              statusText: result.statusText,
              url: result.url,
            },
            transId,
          );
          reject(
            getStandardErrorObject(
              ClientErrors.POST_PREFERENCES_API_FAILED,
              transId,
            ),
          );
        }
      })
      .catch((e) => {
        console.log(e);
        // Get a transId to link errors from the client to splunk
        const transId = getTransactionId();
        logError(
          ClientErrors.POST_PREFERENCES_API_FAILED,
          {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
            ...(e.response ?? {}),
          },
          transId,
        );
        reject(
          getStandardErrorObject(
            ClientErrors.POST_PREFERENCES_API_FAILED,
            transId,
          ),
        );
      });
  });
}

export async function postSalesforceData(currentUser, cookies) {
  if (cookies.Advertising === false || cookies.SaleOfInfo === false) {
    const data = {
      billingAddress: {
        city: null,
        countryCode: null,
        postalCode: null,
        state: null,
        streetAddress1: null,
        streetAddress2: null,
      },
      email: currentUser.email,
      firstName: currentUser.given_name,
      lastName: currentUser.family_name,
      telephoneNumber: null,
    };
    const formIdMapping = FORM_INPUT_ID_MAPPING[ENV];
    const actionSelectList = {
      type: 'Do Not Share or Sell',
      title: 'Do Not Sell or Share Request   ',
      label:
        'I want to prevent sale or share of the personal information that Shutterfly has collected about me or my minor child. Please note that Shutterfly does not process sensitive personal information about you.',
      isChecked: true,
    };
    const mappedData = {};
    const flattenUserInfoObject = flattenObject(data);
    for (let k in flattenUserInfoObject) {
      if (flattenUserInfoObject.hasOwnProperty(k)) {
        let key = formIdMapping[k];
        if (k === 'firstName') {
          mappedData[key] =
            flattenUserInfoObject[k] !== null
              ? flattenUserInfoObject[k]
              : currentUser.given_name;
        } else if (k === 'lastName') {
          mappedData[key] =
            flattenUserInfoObject[k] !== null
              ? flattenUserInfoObject[k]
              : currentUser.family_name;
        } else if (k === 'countryCode' || k === 'state') {
          mappedData[key] =
            flattenUserInfoObject[k] !== null ? flattenUserInfoObject[k] : '';
        } else {
          mappedData[key] = flattenUserInfoObject[k];
        }
      }
    }

    mappedData[formIdMapping['userId']] = currentUser.sfly_uid;
    mappedData[formIdMapping['requestType']] = actionSelectList.type;
    mappedData['orgid'] = formIdMapping['orgid'];
    mappedData['recordType'] = formIdMapping['recordType'];
    mappedData[formIdMapping['isCCPA']] = 'true';
    try {
      await postRequestForm(mappedData);
    } catch (error) {
      throw new Error('salesforce API fail');
    }
  }
}
